import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminOnly } from './components/AdminOnly.js';
import { AppShell } from './components/AppShell.js';
import { OwnerOnly } from './components/OwnerOnly.js';
import { useAuth } from './lib/auth.js';
import { AcceptInvitePage } from './pages/AcceptInvitePage.js';
import { BackfillPage } from './pages/BackfillPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { ClientEditPage } from './pages/ClientEditPage.js';
import { ClientsListPage } from './pages/ClientsListPage.js';
import { ContractorOnboardingPage } from './pages/ContractorOnboardingPage.js';
import { EngagementDetailPage } from './pages/EngagementDetailPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { EulaPage, PrivacyPage } from './pages/LegalPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { OnboardingDetailPage } from './pages/OnboardingDetailPage.js';
import { OnboardingQueuePage } from './pages/OnboardingQueuePage.js';
import { ReferralsPage } from './pages/ReferralsPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { ResourceDetailPage } from './pages/ResourceDetailPage.js';
import { ResourcesListPage } from './pages/ResourcesListPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { VendorDetailPage } from './pages/VendorDetailPage.js';
import { VendorsListPage } from './pages/VendorsListPage.js';

export function AppRoutes() {
  const { admin, loading } = useAuth();

  if (loading) return null;

  // /accept-invite/:token and /onboard/:token are reachable while
  // unauthenticated. Render via Routes so useParams works; everything else
  // falls through to LoginPage if there's no session.
  if (!admin) {
    return (
      <Routes>
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        <Route path="/onboard/:token" element={<ContractorOnboardingPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        {/* Public legal pages — also required as Intuit production URLs (T-079). */}
        <Route path="/legal/eula" element={<EulaPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Contractor onboarding renders chrome-less even for logged-in admins
          (e.g. Eric testing a link) — outside the AppShell layout route. */}
      <Route path="/onboard/:token" element={<ContractorOnboardingPage />} />
      {/* Reachable while authenticated too, so an admin clicking their own emailed
          reset link sees the form instead of being silently redirected home (T-094). */}
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* Public legal pages render chrome-less, available to admins too. */}
      <Route path="/legal/eula" element={<EulaPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route element={<AppShell />}>
        <Route index element={<ResourcesListPage />} />
        <Route path="resources/:id" element={<ResourceDetailPage />} />
        <Route path="vendors" element={<VendorsListPage />} />
        <Route path="vendors/:id" element={<VendorDetailPage />} />
        <Route path="engagements/:id" element={<EngagementDetailPage />} />
        <Route path="onboarding" element={<OnboardingQueuePage />} />
        <Route path="onboarding/:id" element={<OnboardingDetailPage />} />
        <Route
          path="billing"
          element={
            <AdminOnly>
              <BillingPage />
            </AdminOnly>
          }
        />
        <Route
          path="referrals"
          element={
            <AdminOnly>
              <ReferralsPage />
            </AdminOnly>
          }
        />
        <Route
          path="clients"
          element={
            <AdminOnly>
              <ClientsListPage />
            </AdminOnly>
          }
        />
        <Route
          path="backfill"
          element={
            <OwnerOnly>
              <BackfillPage />
            </OwnerOnly>
          }
        />
        <Route
          path="clients/:id"
          element={
            <AdminOnly>
              <ClientEditPage />
            </AdminOnly>
          }
        />
        <Route
          path="settings"
          element={
            <OwnerOnly>
              <SettingsPage />
            </OwnerOnly>
          }
        />
        {/* If an already-authenticated user clicks an invite link, send them home */}
        <Route path="accept-invite/*" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
